import React from 'react';
import FeatureCards from "../../../components/FeatureCards/FeatureCards";
import CTA from "../../../components/Constant/cta/CTA";
import WhySection from "../../../components/WhySection/WhySection";
import LocationTimeline from "../../../components/LocationTimeline/LocationTimeline";
import IntroSection from "../../../components/IntroSection/IntroSection";
import FAQList from "../../../components/Constant/FAQCard/FAQCard";
import InfoBlocks from "../../../components/InfoBlocks/InfoBlocks";
import ServiceCards from "../../../components/ServiceCards/ServiceCards";
import SEO from "../../../utils/SEO";

export default function IndonesiaJurisdiction() {

    return (
        <main className='main'>
            <SEO
                title="Indonesia Jurisdiction | Company & Residency Setup "
                description="Set up companies and obtain residency in Indonesia. PT PMA structures and Investor KITAS for foreign investors in a fast-growing economy."
                keywords="Indonesia company formation, PT PMA Indonesia, Investor KITAS Indonesia, Indonesia residency visa, foreign investment Indonesia, Bali business setup, Batam company formation, Indonesia investment opportunities "
                url="/indonesia-jurisdiction"
            />
            <IntroSection
                title="Indonesia Jurisdiction"
                subtitle="Company Formation, Residency & Investment in Indonesia"
                description="Indonesia has become a rising destination for foreign investors, especially in Bali, Jakarta, and Batam, thanks to its fast-growing economy, large population (270M+), and pro-investment policies. Through PT PMA companies and residency programs like the Investor KITAS, foreign entrepreneurs can establish businesses and secure long-term residency."
                badges={[
                    "PT PMA Company Formation",
                    "Investor KITAS Residency",
                    "Growing FinTech & Digital Economy"
                ]}
                buttonText="Request an Indonesia Jurisdiction Proposal →"
                buttonLink="/quote"
                imageSrc={require('../../../assets/images/jurisdictions/indonesia-jurisdiction.png')}
                imageAlt="Indonesia Company Formation & Residency"
            />


            <section className='section'>
                <div className="container">

                    <WhySection
                        title='Why Choose Indonesia?'
                        blocks={[
                            {
                                items: [
                                    {
                                        title: "Largest Economy",
                                        text: "Largest economy in Southeast Asia."
                                    },
                                    {
                                        title: "Investor-Friendly",
                                        text: "PMA companies allow 100% foreign ownership in many sectors."
                                    },
                                    {
                                        title: "Residency Options",
                                        text: "Investor KITAS, Retirement Visa, and new Second Home Visa."
                                    },
                                    {
                                        title: "Strategic Location",
                                        text: "Access to ASEAN markets."
                                    },
                                    {
                                        title: "Low Costs",
                                        text: "Incorporation and living costs are lower than Singapore or Hong Kong."
                                    },
                                    {
                                        title: "Booming Digital Market",
                                        text: "Strong growth in fintech, crypto, and e-commerce."
                                    }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Company Formation in Indonesia"
                        blocks={[
                            {
                                title: "PT PMA (Perseroan Terbatas Penanaman Modal Asing)",
                                items: [
                                    { text: "Legal entity for foreign-owned businesses." },
                                    { text: "Minimum 2 shareholders (can be individuals or companies)." },
                                    { text: "Requires at least 1 director and 1 commissioner." },
                                    { text: "Minimum capital requirements depend on business sector (USD 100k+ for many)." },
                                    { text: "Eligible for Investor KITAS residency visa." }
                                ]
                            },
                            {
                                title: "Local PT Company",
                                items: [
                                    { text: "Owned by Indonesian nationals." },
                                    { text: "Foreigners can partner through joint ventures." }
                                ]
                            },
                            {
                                title: "Representative Office (KPPA)",
                                items: [
                                    { text: "For market research or liaison." },
                                    { text: "Cannot generate local revenue." }
                                ]
                            }
                        ]}
                    />

                    <LocationTimeline
                        title="Residency & Visa Options"
                        item={[
                            {
                                title: "Investor KITAS",
                                text: "Residency linked to PT PMA shareholding (2–5 years)."
                            },
                            {
                                title: "Retirement Visa",
                                text: "Available from age 55+."
                            },
                            {
                                title: "Second Home Visa",
                                text: "5–10 year residency for investors meeting financial criteria."
                            },
                            {
                                title: "Work KITAS",
                                text: "For employees sponsored by local companies."
                            }
                        ]}
                    />

                    <ServiceCards
                        title='Licenses Available in Indonesia'
                        services={[
                            {
                                title: "Business Licenses",
                                text: "Regulated by the OSS (Online Single Submission) system."
                            },
                            {
                                title: "FinTech & Payments",
                                text: "Overseen by OJK (Financial Services Authority) and Bank Indonesia."
                            },
                            {
                                title: "Crypto Registration",
                                text: "Regulated under Bappebti (Commodity Futures Trading Regulatory Agency)."
                            },
                            {
                                title: "Tourism Licenses",
                                text: "For hotels, resorts, and travel agencies in Bali & beyond."
                            }
                        ]}
                    />

                    <FeatureCards
                        title='Advantages of Indonesia Jurisdiction'
                        features={[
                            {
                                title: "Gateway to ASEAN",
                                text: "Free trade within Southeast Asia."
                            },
                            {
                                title: "Lifestyle Destination",
                                text: "Bali is a global hub for expats, startups, and investors."
                            },
                            {
                                title: "Emerging Market Growth",
                                text: "Strong demand in fintech, real estate, and tourism."
                            },
                            {
                                title: "Residency by Investment",
                                text: "Investor KITAS allows long-term stay."
                            },
                            {
                                title: "Legal Certainty",
                                text: "Structured regulations for foreign investment."
                            }
                        ]}
                    />

                    <FAQList data={faqData} title='Frequently Asked Questions (FAQ)'/>

                    <CTA
                        title="Ready to launch your business in Indonesia?"
                        description="Offshore Advisory provides PT PMA company formation, Investor KITAS residency, and fintech/crypto licensing support."
                        buttonText="Book a Free Consultation for Indonesia Today →"
                        buttonLink="/quote"
                    />

                </div>
            </section>
        </main>
    );
};

const faqData = [
    {
        question: "Can foreigners own companies in Indonesia?",
        answer: "Yes, through PT PMA structures."
    },
    {
        question: "What is the minimum capital for PT PMA?",
        answer: "Generally USD 100,000+ depending on the business sector."
    },
    {
        question: "Do I need a local partner?",
        answer: "No, PT PMA allows 100% foreign ownership in many industries."
    },
    {
        question: "How long does incorporation take?",
        answer: "Typically 4–6 weeks."
    },
    {
        question: "Can I get residency through investment?",
        answer: "Yes, the Investor KITAS is tied to PT PMA ownership."
    }
];
